import React from 'react';
import { Link } from 'react-router-dom';
import { Activity, Gauge, HeartPulse, Sparkles, Users, Target, Heart, ArrowRight } from 'lucide-react';
import { siteData } from '../data/mock';
import SEO from '../components/SEO';

const iconMap = {
  'activity': Activity,
  'gauge': Gauge,
  'heart-pulse': HeartPulse,
  'sparkles': Sparkles
};

const LecentrePage = () => {
  return (
    <div style={{ marginTop: '100px' }}>
      <SEO
        title="Le Centre — Nutri Centre Riviera, approche pluridisciplinaire"
        description="Centre pluridisciplinaire de nutrition et santé féminine sur la Riviera vaudoise. Notre approche, notre vision, nos valeurs. Vevey, Clarens, Montreux — canton de Vaud."
        keywords="centre nutrition Vevey, approche pluridisciplinaire Clarens, vision Nutri Centre Riviera, santé femme Vaud"
        url="https://nutricentreriviera.ch/le-centre"
      />
      {/* Hero Section */}
      <section style={{
        background: 'var(--gradient-hero)',
        padding: '6rem 0 4rem',
        textAlign: 'center'
      }}>
        <div className="container">
          <h1 className="heading-hero" style={{ marginBottom: '1.5rem' }}>
            {siteData.aboutCenter.title}
          </h1>
          <p className="heading-subtitle" style={{ maxWidth: '800px', margin: '0 auto' }}>
            Un espace dédié à votre santé et votre bien-être
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section" style={{ background: 'white' }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1.2fr 1fr',
            gap: '4rem',
            alignItems: 'start'
          }}>
            {/* Left Content */}
            <div>
              <div className="body-large" style={{ 
                whiteSpace: 'pre-line', 
                marginBottom: '3rem',
                lineHeight: 1.8
              }}>
                {siteData.aboutCenter.description}
              </div>

              {/* Values */}
              <div style={{ marginTop: '3rem' }}>
                <h3 className="heading-2" style={{ marginBottom: '2rem' }}>
                  Nos valeurs
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                    <div style={{
                      width: '3rem',
                      height: '3rem',
                      borderRadius: '0.75rem',
                      background: 'var(--accent-primary-light)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--accent-primary)',
                      flexShrink: 0
                    }}>
                      <Users size={24} />
                    </div>
                    <div>
                      <h4 className="heading-3" style={{ marginBottom: '0.5rem' }}>Approche personnalisée</h4>
                      <p className="body-medium">
                        Chaque femme est unique. Nous adaptons nos soins à vos besoins spécifiques pour un accompagnement sur mesure.
                      </p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                    <div style={{
                      width: '3rem',
                      height: '3rem',
                      borderRadius: '0.75rem',
                      background: 'var(--accent-primary-light)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--accent-primary)',
                      flexShrink: 0
                    }}>
                      <Target size={24} />
                    </div>
                    <div>
                      <h4 className="heading-3" style={{ marginBottom: '0.5rem' }}>Expertise pluridisciplinaire</h4>
                      <p className="body-medium">
                        Une équipe de professionnels qualifiés travaillant en synergie pour votre santé globale.
                      </p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                    <div style={{
                      width: '3rem',
                      height: '3rem',
                      borderRadius: '0.75rem',
                      background: 'var(--accent-primary-light)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--accent-primary)',
                      flexShrink: 0
                    }}>
                      <Heart size={24} />
                    </div>
                    <div>
                      <h4 className="heading-3" style={{ marginBottom: '0.5rem' }}>Accompagnement bienveillant</h4>
                      <p className="body-medium">
                        Un environnement chaleureux et à l'écoute pour vous sentir en confiance tout au long de votre parcours.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Sidebar */}
            <div style={{
              position: 'sticky',
              top: '100px'
            }}>
              <div style={{
                background: 'var(--bg-page)',
                borderRadius: '1rem',
                padding: '2rem',
                border: '1px solid var(--border-light)'
              }}>
                <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>
                  Notre approche
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {siteData.services.map((service) => {
                    const IconComponent = iconMap[service.icon];
                    return (
                      <div key={service.id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        padding: '1rem',
                        background: 'white',
                        borderRadius: '0.75rem',
                        border: '1px solid var(--border-light)'
                      }}>
                        <div style={{
                          width: '2.5rem',
                          height: '2.5rem',
                          borderRadius: '0.5rem',
                          background: 'var(--accent-primary-light)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'var(--accent-primary)',
                          flexShrink: 0
                        }}>
                          <IconComponent size={20} strokeWidth={1.5} />
                        </div>
                        <span className="body-medium" style={{ fontWeight: 500 }}>
                          {service.title}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <a
                href="https://www.onedoc.ch/fr/cabinet-de-groupe/vevey/ebdj4/nutri-centre-riviera"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                style={{ marginTop: '1.5rem', width: '100%', justifyContent: 'center', textDecoration: 'none' }}
              >
                Prendre rendez-vous
                <ArrowRight size={20} />
              </a>
            </div>
          </div>

          {/* Responsive Mobile */}
          <style>{`
            @media (max-width: 968px) {
              .container > div:first-child {
                grid-template-columns: 1fr !important;
              }
              .container > div:first-child > div:last-child {
                position: static !important;
              }
            }
          `}</style>
        </div>
      </section>

      {/* Galerie du cabinet */}
      <section className="section" style={{ background: 'var(--bg-page)', padding: '5rem 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '780px', margin: '0 auto 3rem' }}>
            <span className="caption" style={{
              color: 'var(--accent-primary)',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              fontWeight: 600,
              fontSize: '0.85rem',
              display: 'inline-block',
              marginBottom: '1rem'
            }}>
              Nos espaces
            </span>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Un cabinet lumineux face au lac et aux montagnes
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
              Un environnement calme et chaleureux, pensé pour votre bien-être à chaque étape de votre accompagnement.
            </p>
          </div>

          <div
            className="cabinet-gallery"
            style={{
              display: 'grid',
              gridTemplateColumns: '1.4fr 1fr',
              gridTemplateRows: 'repeat(2, 280px)',
              gap: '1.25rem'
            }}
          >
            {/* Grande image principale */}
            <div
              data-testid="cabinet-photo-1"
              style={{
                gridRow: 'span 2',
                position: 'relative',
                borderRadius: '1rem',
                overflow: 'hidden',
                boxShadow: '0 14px 40px rgba(20, 45, 35, 0.12)',
                background: 'var(--bg-section)'
              }}
            >
              <img
                src="/images/cabinet-vevey-2.webp"
                alt="Salle de consultation avec vue sur le Lac Léman et les montagnes — Nutri Centre Riviera, Vevey"
                loading="lazy"
                decoding="async"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </div>

            {/* Image haut-droite */}
            <div
              data-testid="cabinet-photo-2"
              style={{
                position: 'relative',
                borderRadius: '1rem',
                overflow: 'hidden',
                boxShadow: '0 14px 40px rgba(20, 45, 35, 0.12)',
                background: 'var(--bg-section)'
              }}
            >
              <img
                src="/images/cabinet-vevey-1.webp"
                alt="Cabinet de consultation lumineux avec vue panoramique sur les Alpes — Nutri Centre Riviera"
                loading="lazy"
                decoding="async"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </div>

            {/* Image bas-droite */}
            <div
              data-testid="cabinet-photo-3"
              style={{
                position: 'relative',
                borderRadius: '1rem',
                overflow: 'hidden',
                boxShadow: '0 14px 40px rgba(20, 45, 35, 0.12)',
                background: 'var(--bg-section)'
              }}
            >
              <img
                src="/images/cabinet-clarens.webp"
                alt="Salle d'attente épurée et accueillante — Nutri Centre Riviera, Vevey"
                loading="lazy"
                decoding="async"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </div>
          </div>

          <style>{`
            @media (max-width: 768px) {
              .cabinet-gallery {
                grid-template-columns: 1fr !important;
                grid-template-rows: auto !important;
                gap: 1rem !important;
              }
              .cabinet-gallery > div {
                grid-row: auto !important;
                height: 260px;
              }
            }
          `}</style>
        </div>
      </section>
    </div>
  );
};

export default LecentrePage;
